import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ReactComponent as MoreOptionsIcon } from '../../../../assets/svg/more-options.svg';
import { useUser } from '../../../../hooks/useUser';
import { isInternal, isProTeam } from '../../../../utils';

import {
  IconAddGroup,
  IconRemoveGroup,
  IconEye,
  IconPencil,
  IconTrash,
  IconShare,
  IconLink,
  DraggerIcon,
} from '../../../../utils/icons';
import * as Reorder from '../../../../components/dnd/Reorder';
import { ISinglePro } from '../../../../components/singlePro/state';
import { ExpertCardContent } from './ExpertCardContent';

interface ListProCardProps {
  pro: ISinglePro;
  index?: number;
  deleteUser: (userid: string) => void;
  addToShortlist: (pro: any) => void;
  removeFromShortlist: (userid: string) => void;
  isExpertPage: boolean;
  isShortlistPage: boolean;
  onSelect: (userid: number, checked: boolean) => void;
  selected: boolean;
  createAutoLoginUrl: (pro: any) => void;
  isSharePath: boolean;
  enableDrag?: boolean;
}

export default function ListProCard(props: ListProCardProps) {
  const {
    pro,
    index,
    deleteUser,
    addToShortlist,
    removeFromShortlist,
    isExpertPage,
    isShortlistPage,
    onSelect,
    selected,
    createAutoLoginUrl,
    isSharePath = false,
    enableDrag = false,
  } = props;
  const { role } = useUser();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [isCardHovered, setIsCardHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const onMenuClick = (ev?: any) => setAnchorEl(ev?.currentTarget);
  const navigate = useNavigate();

  const { userid } = pro;
  if (!userid) return null;
  const userIdString = userid.toString();

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(userid, event.target.checked);
  };

  const handleCardClick = () => {
    if (isSharePath) {
      navigate(`/experts/share?proId=${userid}`);
      return;
    }
    navigate(`/experts/${userid}/${pro.slug || "-"}?proId=${userid}`);
  };

  const getMenuItem = (
    Icon: any,
    text: string,
    href?: string,
    onClick?: () => void
  ) => (
    <MenuItem
      onClick={() => {
        onClick?.();
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      }}
      className="!p-1.5 !px-4 flex gap-2 items-center"
      // `stroke` is an SVG presentation attribute and cannot resolve var(),
      // so the token is set here and picked up through currentColor.
      sx={{ color: 'var(--text-2)' }}
    >
      <Icon width={19} height={19} size={19} stroke="currentColor" />
      <Typography fontFamily="CircularStd" className="text-text-2 text-xs">
        {text}
      </Typography>
    </MenuItem>
  );

  const cardContent = (
    <div
      ref={containerRef}
      className={classNames('flex flex-row w-full gap-2 sm:gap-4 items-center')}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
    >
      {/* Same guard as ProCard: no selection handler → no checkbox. */}
      {!isSharePath && typeof onSelect === "function" && (
        <div className="flex items-center shrink-0">
          <Checkbox
            checked={selected}
            onChange={handleCheckboxChange}
            className="!p-0 rounded-lg"
            sx={{
              '& .MuiSvgIcon-root': { fill: 'var(--muted)' },
              '&.Mui-checked .MuiSvgIcon-root': { fill: 'var(--accent)' },
            }}
          />
        </div>
      )}

      <div
        className={classNames(
          'flex gap-3 sm:gap-4 w-full min-w-0 px-3 sm:px-4 py-4 sm:py-6 border-b border-border-token relative rounded-lg',
          { 'bg-surface': !isCardHovered, 'bg-surface-2': isCardHovered }
        )}
      >
        <ExpertCardContent
          pro={pro}
          onNameClick={handleCardClick}
          onAvatarClick={handleCardClick}
          showEngagement={isInternal(role)}
          showId={isInternal(role)}
          copyableId
        />
        {!isSharePath && (
          <div className="absolute top-5 right-4 flex h-fit">
            <div className="flex gap-2 justify-end items-center">
              {enableDrag && index !== undefined && (
                <Reorder.Trigger index={index} containerRef={containerRef}>
                  <Box className="flex items-center justify-center cursor-pointer p-1">
                    <DraggerIcon size={26} stroke="#C6C6C6" />
                  </Box>
                </Reorder.Trigger>
              )}
              <IconButton id="basic-button" onClick={onMenuClick}>
                <MoreOptionsIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => onMenuClick()}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                MenuListProps={{ 'aria-labelledby': 'basic-button' }}
              >

                {getMenuItem(
                  IconEye,
                  'View',
                  `/experts/${userIdString}/${
                    pro.slug || '-'
                  }/?proId=${userIdString}`
                )}

                {isProTeam(role) && (
                  <>
                    {getMenuItem(
                      IconPencil,
                      'Edit',
                      `/experts/${userIdString}/${pro.slug || '-'}/edit`
                    )}
                    {!isShortlistPage &&
                      getMenuItem(IconTrash, "Delete", undefined, () =>
                        deleteUser(userIdString)
                      )}
                  </>
                )}

                {pro?.status === "Active" &&
                  getMenuItem(
                    IconShare,
                    'Share URL',
                    `/experts/share?proId=${userIdString}`
                  )}

                {isInternal(role) &&
                  getMenuItem(IconLink, 'Auto Login URL', undefined, () =>
                    createAutoLoginUrl(pro)
                  )}

                {isInternal(role) &&
                  addToShortlist &&
                  pro.status === 'Active' &&
                  isExpertPage &&
                  getMenuItem(IconAddGroup, 'Add to Shortlist', undefined, () =>
                    addToShortlist(pro)
                  )}

                {isInternal(role) &&
                  removeFromShortlist &&
                  isShortlistPage &&
                  getMenuItem(
                    IconRemoveGroup,
                    'Remove from Shortlist',
                    undefined,
                    () => removeFromShortlist(userIdString)
                  )}
              </Menu>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return cardContent;
}
